import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Pressable, FlatList, ActivityIndicator, KeyboardAvoidingView, Platform, StyleSheet, Alert } from 'react-native';
import { Text, TextInput } from '../ui/Text';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Clipboard from 'expo-clipboard';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import {
  useRoom, useThread, useTimeline, useStoreVersion, loadThread, sendText, getReactions, sendReaction, removeReaction, useIsAgent, senderName,
  isGate, isGateOpen, isGateOpenAmong, isAnsweredHere, decisionOn, answerGate, GateChoice, MEvent, GATE_LAPSED,
  canDelete, deleteMessage, markThreadRead } from '../matrix';
import { usePrefs } from '../prefs';
import { MessageRow } from '../ui/MessageRow';
import { KeyboardShift } from '../ui/KeyboardShift';
import { Composer } from '../ui/Composer';
import { ActionSheet, SheetAction } from '../ui/ActionSheet';
import { confirmDestructive } from '../ui/confirm';
import { GateBar, DECIDED } from '../ui/GateBar';
import { radius, makeStyles, useTheme } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'Thread'>;
const QUICK_REACTIONS = ['👍', '✅', '❤️', '😂', '👀'];

/** A Matrix thread: the root message pinned on top, replies below, composer replies into the thread. */
export default function ThreadScreen({ route, navigation }: Props) {
  const theme = useTheme(); const s = useStyles();
  const { roomId, rootId } = route.params;
  const { userId } = useAuth();
  const room = useRoom(roomId);
  const { root, replies } = useThread(roomId, rootId);
  const roomTimeline = useTimeline(roomId);
  const inRoom = useMemo(() => new Set(roomTimeline.map((e) => e.id)), [roomTimeline]);
  const isAgent = useIsAgent();
  const { humanBadge } = usePrefs();
  useStoreVersion(); // reactions live outside the timeline — re-render when they change
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState<MEvent | null>(null);
  const listRef = useRef<FlatList<MEvent>>(null);

  useEffect(() => { navigation.setOptions({ title: root?.thread?.title || (room?.name ? `Thread · ${room.name}` : 'Thread') }); }, [room?.name, root?.thread?.title]);
  useEffect(() => { loadThread(roomId, rootId).catch(() => {}).finally(() => setLoading(false)); }, [roomId, rootId]);
  useEffect(() => { markThreadRead(roomId, rootId); }, [roomId, rootId, replies.length]);

  const send = async () => {
    const body = text.trim(); if (!body || sending) return;
    setSending(true); setText('');
    try { await sendText(roomId, body, { threadRoot: rootId }); listRef.current?.scrollToEnd({ animated: true }); }
    catch (e: any) { setText(body); Alert.alert('Could not send', e?.message); }
    finally { setSending(false); }
  };
  const react = (ev: MEvent, key: string) => {
    const mine = (getReactions(ev.id)[key] || []).includes(userId || '');
    (mine ? removeReaction(roomId, ev.id, key) : sendReaction(roomId, ev.id, key)).catch(() => {});
  };
  const onLongPress = (ev: MEvent) => { if (!ev.pending) setSheet(ev); };
  const answer = (ev: MEvent, choice: GateChoice, note?: string) => { answerGate(roomId, ev, choice, note).catch(() => {}); };
  const confirmDelete = (ev: MEvent) => confirmDestructive('Delete this message?', 'It is removed for everyone in the room and cannot be brought back.', 'Delete',
    () => { deleteMessage(roomId, ev.id).catch((e) => Alert.alert('Could not delete', e?.message)); });
  const sheetActions = (ev: MEvent): SheetAction[] => {
    const acts: SheetAction[] = ev.kind === 'text' ? [{ label: 'Copy text', onPress: () => { Clipboard.setStringAsync(ev.body).catch(() => {}); } }] : [];
    if (canDelete(room, ev) && ev.id !== rootId) acts.push({ label: 'Delete message', destructive: true, hint: ev.sender === userId ? 'Removes it for everyone' : 'You can remove this as a room admin', onPress: () => confirmDelete(ev) });
    return acts;
  };
  const byId = useMemo(() => { const m = new Map<string, MEvent>(); replies.forEach((e) => m.set(e.id, e)); if (root) m.set(root.id, root); return m; }, [replies, root]);

  return (
    <SafeAreaView style={s.root} edges={['left', 'right', 'bottom']}>
      <KeyboardShift iosBehavior="padding" iosOffset={90}>
        <FlatList
          ref={listRef}
          data={replies}
          keyExtractor={(e) => e.id}
          contentContainerStyle={{ padding: 12, paddingBottom: 8 }}
          onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: false })}
          ListHeaderComponent={
            <View style={s.rootBox}>
              <Text style={s.rootLabel}>{({ conversation: 'CONVERSATION START', build: 'BUILD THREAD', fix: 'FIX THREAD' } as Record<string, string>)[root?.thread?.kind || ''] || 'THREAD START'}</Text>
              {root ? (
                <MessageRow item={root} room={room} userId={userId} cont={false} isAgent={isAgent} humanBadge={humanBadge} onLongPress={onLongPress} hideQuote />
              ) : <ActivityIndicator color={theme.accent} />}
              <View style={s.divider} />
              <Text style={s.count}>{loading && replies.length === 0 ? 'Loading replies…' : `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}</Text>
            </View>
          }
          ListEmptyComponent={!loading ? <Text style={s.empty}>{root?.thread?.kind === 'conversation' ? 'Nothing here yet. Write your first message below.' : 'No replies yet. Write below to start the thread.'}</Text> : null}
          renderItem={({ item, index }) => {
            const prev = replies[index - 1];
            const cont = !!prev && prev.sender === item.sender && item.ts - prev.ts < 5 * 60 * 1000;
            const rx = Object.entries(getReactions(item.id)).filter(([, who]) => who.length);
            const gate = isGate(item);
            // Gates that arrived live sit in the room timeline (judge them there, like Home does);
            // ones only fetched for this thread are judged among the thread's replies.
            const open = gate && (inRoom.has(item.id) ? isGateOpen(roomId, item) : isGateOpenAmong(item, replies));
            const quoted = item.replyTo ? byId.get(item.replyTo) : undefined;
            const hideQuote = !!quoted && isAgent(item.sender) && (quoted.id === rootId || quoted.id === replies[index - 1]?.id);
            return (
              <MessageRow
                item={item} room={room} userId={userId} cont={cont} isAgent={isAgent} humanBadge={humanBadge}
                quoted={quoted} hideQuote={hideQuote} onLongPress={onLongPress}
                onCardAction={(value) => { sendText(roomId, value, { threadRoot: rootId }).catch(() => {}); }}
                footer={(
                  <>
                    {rx.length > 0 && (
                      <View style={s.rxRow}>
                        {rx.map(([key, who]) => (
                          <Pressable key={key} onPress={() => react(item, key)} style={[s.rx, who.includes(userId || '') && s.rxMine]}>
                            <Text style={s.rxText}>{key} {who.length}</Text>
                          </Pressable>
                        ))}
                      </View>
                    )}
                    {gate && open && <GateBar always={!item.card} about={item.card?.title} onAnswer={(c, note) => answer(item, c, note)} />}
                    {gate && !open && (decisionOn(item, replies) || isAnsweredHere(item.id)) && <Text style={s.answered}>{DECIDED[decisionOn(item, replies) as GateChoice] || '✓ Answered'}</Text>}
                    {gate && !open && !item.card && !decisionOn(item, replies) && !isAnsweredHere(item.id) && <Text style={s.lapsed}>{GATE_LAPSED}</Text>}
                  </>
                )}
              />
            );
          }}
        />
        <Composer value={text} onChangeText={setText} onSend={send} sending={sending}
          placeholder={root?.thread?.kind === 'conversation' && room?.name ? `Message ${room.name}` : 'Reply in thread'} />
      </KeyboardShift>

      <ActionSheet
        visible={!!sheet}
        title={sheet ? (sheet.sender === userId ? 'You' : senderName(room, sheet.sender)) : undefined}
        subtitle={sheet ? sheet.body.replace(/\s+/g, ' ').slice(0, 120) : undefined}
        reactions={QUICK_REACTIONS} onReact={(k) => { if (sheet) react(sheet, k); }}
        actions={sheet ? sheetActions(sheet) : []} onClose={() => setSheet(null)}
      />
    </SafeAreaView>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  rootBox: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.lg, padding: 12, paddingTop: 10 },
  rootLabel: { color: theme.faint, fontSize: 10.5, fontWeight: '700', letterSpacing: 1, fontFamily: theme.mono, marginBottom: 2 },
  divider: { height: 1, backgroundColor: theme.border, marginTop: 12, marginBottom: 8 },
  count: { color: theme.faint, fontSize: 12, fontFamily: theme.mono },
  empty: { color: theme.faint, fontSize: 14, textAlign: 'center', paddingVertical: 24 },
  rxRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  rx: { backgroundColor: theme.surface2, borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: theme.border },
  rxMine: { borderColor: theme.accent, backgroundColor: theme.accentBg },
  rxText: { color: theme.ink, fontSize: 12 },
  answered: { color: theme.good, fontSize: 12, fontWeight: '600', marginTop: 6 },
  lapsed: { color: theme.faint, fontSize: 12.5, marginTop: 6, fontStyle: 'italic' },
}));
